import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Case, User } from "pwapi";

import { CaseDiagnosis } from "custom-models";

import { optimisticDiagnosisUpdate } from "shared/actions/specimenDetailActions";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { ApplicationState } from "shared/testUtils";

import "./FinalizedDiagnosisDialog.css";

import AddendDiagnosisDialog from "./AddendDiagnosisDialog";
import CopiedDiagnosisDialog from "./CopiedDiagnosisDialog";
import ReadOnlyDiagnosisDialog from "./ReadOnlyDiagnosisDialog";
import WPLAddonMessageDialog from "./WPLAddonMessageDialog";
import { DialogEnum } from "../Dialog";
import {
  canAmendOrAddend,
  canCorrectReport,
  getHighestVersionDiagnosis,
  isScreened,
} from "../utils";

type FinalizedDiagnosisDialogProps = {
  caseDetailObject: CaseDetailState;
  slideDetailObject: SlideDetailState;
  user?: User;
  dialog?: DialogEnum | null;
  setDialog?: Function;
};

const FinalizedDiagnosisDialog = ({
  caseDetailObject,
  slideDetailObject,
  user,
  dialog,
  setDialog,
}: FinalizedDiagnosisDialogProps) => {
  const dispatch = useDispatch();

  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );

  const specimenDiagnoses = useMemo(() => {
    return caseDetailObject.specimenDetail?.find(
      (specimen) => specimen.id === slideDetailObject.specimen?.id
    )?.diagnoses;
  }, [caseDetailObject]);

  const incompleteDiagnosis = useMemo<CaseDiagnosis | undefined>(() => {
    return specimenDiagnoses?.find(
      (diag) => diag.user && diag.user.id === user?.id && !diag.is_completed
    );
  }, [caseDetailObject]);

  const completeDiagnosis =
    specimenDiagnoses && getHighestVersionDiagnosis(specimenDiagnoses);

  const updateDiagnosis = (field: string, value: string) => {
    incompleteDiagnosis?.id &&
      slideDetailObject.specimen &&
      dispatch(
        optimisticDiagnosisUpdate(
          // @ts-ignore
          { ...incompleteDiagnosis, [field]: value },
          slideDetailObject.specimen
        )
      );
  };

  const resetDiagnosis = () => {
    completeDiagnosis &&
      incompleteDiagnosis?.id &&
      slideDetailObject.specimen &&
      dispatch(
        optimisticDiagnosisUpdate(
          // @ts-ignore
          {
            ...incompleteDiagnosis,
            diagnosis: completeDiagnosis.diagnosis,
            microscopic: completeDiagnosis.microscopic,
            comment: completeDiagnosis.comment,
            icd10_code: completeDiagnosis.icd10_code,
            zsd_code: completeDiagnosis.zsd_code,
            //@ts-ignore
            cpt_codes: completeDiagnosis.cpt_codes,
          },
          slideDetailObject.specimen
        )
      );
  };

  const getDialogHeader = useCallback(
    (incompleteDiagSour: string, completeDiagSour?: string) => {
      return incompleteDiagSour === CaseDiagnosis.source.AMEND ||
        incompleteDiagSour === CaseDiagnosis.source.ADDEND
        ? `You are ${
            incompleteDiagSour === CaseDiagnosis.source.AMEND
              ? "amending"
              : "addending"
          } the diagnosis for Specimen ${
            slideDetailObject.specimen?.name || ""
          }`
        : completeDiagSour === CaseDiagnosis.source.SCREEN &&
          incompleteDiagSour === CaseDiagnosis.source.FINALIZE
        ? `${caseDetailObject.accession} has been screened by ${caseDetailObject.screen_assignment?.first_name} ${caseDetailObject.screen_assignment?.last_name}`
        : `You are correcting the report for ${caseDetailObject.accession}`;
    },
    [caseDetailObject?.id, slideDetailObject?.id]
  );

  return completeDiagnosis && incompleteDiagnosis ? (
    incompleteDiagnosis.source === CaseDiagnosis.source.ADDEND ? (
      <AddendDiagnosisDialog
        hat={getDialogHeader(incompleteDiagnosis.source)}
        completeDiagnosis={completeDiagnosis}
        addendDiagnosis={incompleteDiagnosis}
        onUpdateDiagnosis={updateDiagnosis}
        isModMedCase={!!caseDetailObject.patient?.clinic?.modmed_clinic_id}
      />
    ) : (
      <CopiedDiagnosisDialog
        hat={getDialogHeader(
          incompleteDiagnosis.source,
          completeDiagnosis.source
        )}
        completeDiagnosis={completeDiagnosis}
        copiedDiagnosis={incompleteDiagnosis}
        onUpdateDiagnosis={updateDiagnosis}
        onResetDiagnosis={resetDiagnosis}
        isCorrection={
          incompleteDiagnosis.source === CaseDiagnosis.source.CORRECTION
        }
        hasScreenDisclaimer={
          completeDiagnosis.source === CaseDiagnosis.source.SCREEN
        }
      />
    )
  ) : caseDetailObject.source === Case.source.WPL &&
    caseDetailObject.case_addon &&
    caseDetailObject.case_addon.length > 0 ? (
    <WPLAddonMessageDialog caseDetail={caseDetailObject} />
  ) : (
    <ReadOnlyDiagnosisDialog
      caseDetailObject={caseDetailObject}
      slideDetailObject={slideDetailObject}
      user={user}
      canAmendAddend={canAmendOrAddend(caseDetailObject, user)}
      canCorrectReport={canCorrectReport(caseDetailObject, features, user)}
      isSpecimenScreened={completeDiagnosis?.source === "screen"}
      isCaseScreened={isScreened(caseDetailObject.case_diagnosis)}
      hat={
        completeDiagnosis?.source === "screen"
          ? {
              text: getDialogHeader(
                CaseDiagnosis.source.FINALIZE,
                completeDiagnosis.source
              ),
              style: "edit",
            }
          : !incompleteDiagnosis && isScreened(caseDetailObject.case_diagnosis)
          ? {
              text: `Specimen ${slideDetailObject.specimen?.name} has not been screened`,
              style: "warning",
            }
          : undefined
      }
      dialog={dialog}
      setDialog={setDialog}
    />
  );
};

export default FinalizedDiagnosisDialog;
